import React, { useState, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { authenticationAtom } from '../../State';
import { useModalContext } from '../../components/ModalProvider';
import { findWorkflowsUsingTool, getWorkflowNavigationUrl, WorkflowUsage } from '../utilities/workflowUsageUtils';

import { Loader2, Workflow } from 'lucide-react';

interface ToolUsageListProps {
    toolId: string;
    hasUnsavedChanges?: boolean;
    onSave?: () => Promise<void>;
}

export const ToolUsageList: React.FC<ToolUsageListProps> = ({
    toolId,
    hasUnsavedChanges = false,
    onSave
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [authentication] = useAtom(authenticationAtom) as any;
    const { showConfirmationDialog } = useModalContext() as any;
    const [usages, setUsages] = useState<WorkflowUsage[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (toolId && authentication?.customer) {
            loadUsages();
        }
    }, [toolId, authentication?.customer]);

    const loadUsages = async () => {
        if (!toolId || !authentication?.customer) return;
        
        setLoading(true);
        try {
            const workflowUsages = await findWorkflowsUsingTool(authentication.customer, toolId);
            setUsages(workflowUsages);
        } catch (error) {
            console.error('Failed to load tool usages:', error);
            setUsages([]);
        } finally {
            setLoading(false);
        }
    };

    const navigateToWorkflow = (workflowId: string, nodeId?: string) => {
        const handleNavigation = () => {
            const url = getWorkflowNavigationUrl(workflowId, nodeId);
            navigate(url);
        };

        if (hasUnsavedChanges && onSave) {
            showConfirmationDialog(
                t('ui.unsavedChanges'),
                t('ui.saveBeforeNavigating'),
                async () => {
                    try {
                        await onSave();
                        handleNavigation();
                    } catch (error) {
                        console.error('Failed to save before navigation:', error);
                    }
                }
            );
        } else {
            handleNavigation();
        }
    };

    if (!toolId) {
        return null;
    }

    return (
        <div className="mb-4">
            <div className="mb-2 text-sm font-semibold">
                {t('ui.usedIn')}
            </div>

            {loading ? (
                <div className="flex items-center gap-2">
                    <Loader2 aria-hidden className="h-4 w-4 animate-spin text-primary" />
                    <span className="text-xs text-muted-foreground">
                        {t('ui.loadingUsages')}
                    </span>
                </div>
            ) : usages.length === 0 ? (
                <div className="text-xs italic text-muted-foreground">
                    {t('ui.notUsedInWorkflows')}
                </div>
            ) : (
                <div className="flex flex-col items-start gap-1">
                    {usages.map((usage, index) => (
                        <button
                            type="button"
                            key={`${usage.workflowId}-${usage.nodeId}-${index}`}
                            onClick={() => navigateToWorkflow(usage.workflowId, usage.nodeId)}
                            className="flex items-center gap-1 text-left text-xs text-primary underline-offset-4 hover:underline"
                        >
                            <Workflow aria-hidden className="h-3 w-3 shrink-0" />
                            {usage.workflowName}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};